import { useState } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import type { PreferredCommunication, SubContact } from '../types';

// Add/edit ONE person under an account (Accounts-lifecycle spec — "Account
// Contacts": one account, multiple named contacts). Not backed by its own
// route: the caller owns the full subContacts[] array and PATCHes the whole
// array back to PATCH /api/contacts-identity/:id, same as every other
// extended field — subContacts is not independently addressable on the
// backend (embedded array, see contactSchema in src/db-mongo.js).

interface SubContactFormModalProps {
  open: boolean;
  subContact?: SubContact | null; // null/undefined = add mode
  loading?: boolean;
  onClose: () => void;
  onSave: (value: SubContact) => void;
}

// This drives `contactTypeTag`, not the free-text `department` field below —
// was previously mislabeled "Department" in the form even though it's the
// spec's "Contact type" (gap-audit follow-up).
const CONTACT_TYPE_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'purchase', label: 'Purchase' },
  { value: 'director_owner', label: 'Director / Owner' },
  { value: 'accounts', label: 'Accounts' },
  { value: 'technical', label: 'Technical' },
  { value: 'production', label: 'Production' },
  { value: 'stores', label: 'Stores' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'qc', label: 'QC' },
];

const COMMUNICATION_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'call', label: 'Call' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'visit', label: 'Visit' },
  { value: 'other', label: 'Other' },
];

const EMPTY: SubContact = {
  name: '', designation: '', department: '', contactTypeTag: null,
  isDecisionMaker: false, isPrimaryContact: false,
  phone: '', whatsapp: '', email: '', linkedin: '', address: '',
  preferredCommunication: '', birthday: null, anniversary: null, notes: '',
  lastCommunicationAt: null, nextFollowUpAt: null,
};

/** ISO datetime -> yyyy-mm-dd for a date input; '' when absent/unreadable. */
function toDateInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

export function SubContactFormModal({ open, subContact, loading, onClose, onSave }: SubContactFormModalProps) {
  const isEdit = !!subContact;
  const [form, setForm] = useState<SubContact>(EMPTY);

  const [prevOpen, setPrevOpen] = useState(open);
  const [prevTarget, setPrevTarget] = useState(subContact);
  if (open !== prevOpen || subContact !== prevTarget) {
    setPrevOpen(open);
    setPrevTarget(subContact);
    if (open) setForm(subContact ? { ...EMPTY, ...subContact } : EMPTY);
  }

  const set = <K extends keyof SubContact>(key: K, value: SubContact[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name?.trim()) return;
    onSave({
      ...form,
      name: form.name.trim(),
      designation: form.designation?.trim() || '',
      department: form.department || '',
      phone: form.phone?.trim() || '',
      whatsapp: form.whatsapp?.trim() || '',
      email: form.email?.trim() || '',
      linkedin: form.linkedin?.trim() || '',
      address: form.address?.trim() || '',
      notes: form.notes?.trim() || '',
      birthday: form.birthday || null,
      anniversary: form.anniversary || null,
      lastCommunicationAt: form.lastCommunicationAt || null,
      nextFollowUpAt: form.nextFollowUpAt || null,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Account Contact' : 'Add Account Contact'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button type="submit" form="subcontact-form" loading={loading}>
            {isEdit ? 'Save Changes' : 'Add Contact'}
          </Button>
        </>
      }
    >
      <form id="subcontact-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Name *" name="sc-name" value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} required />
          <Input label="Designation" name="sc-designation" value={form.designation ?? ''} onChange={(e) => set('designation', e.target.value)} placeholder="e.g. Purchase Manager" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Contact Type"
            name="sc-contact-type"
            value={form.contactTypeTag ?? ''}
            onChange={(e) => set('contactTypeTag', (e.target.value || null) as SubContact['contactTypeTag'])}
            options={CONTACT_TYPE_OPTIONS}
          />
          <Input label="Department" name="sc-department" value={form.department ?? ''} onChange={(e) => set('department', e.target.value)} placeholder="e.g. Procurement" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Preferred Communication"
            name="sc-preferred-communication"
            value={form.preferredCommunication ?? ''}
            onChange={(e) => set('preferredCommunication', e.target.value as PreferredCommunication)}
            options={COMMUNICATION_OPTIONS}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Mobile" name="sc-phone" value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />
          <Input label="WhatsApp" name="sc-whatsapp" value={form.whatsapp ?? ''} onChange={(e) => set('whatsapp', e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Email" name="sc-email" type="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} />
          <Input label="LinkedIn" name="sc-linkedin" value={form.linkedin ?? ''} onChange={(e) => set('linkedin', e.target.value)} placeholder="Profile URL" />
        </div>

        <Input label="Address" name="sc-address" value={form.address ?? ''} onChange={(e) => set('address', e.target.value)} />

        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-body-sm text-on-surface">
            <input type="checkbox" checked={!!form.isDecisionMaker} onChange={(e) => set('isDecisionMaker', e.target.checked)} />
            Decision maker
          </label>
          <label className="flex items-center gap-2 text-body-sm text-on-surface">
            <input type="checkbox" checked={!!form.isPrimaryContact} onChange={(e) => set('isPrimaryContact', e.target.checked)} />
            Primary contact
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Birthday" name="sc-birthday" type="date" value={toDateInput(form.birthday)} onChange={(e) => set('birthday', e.target.value || null)} />
          <Input label="Anniversary" name="sc-anniversary" type="date" value={toDateInput(form.anniversary)} onChange={(e) => set('anniversary', e.target.value || null)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Last Communication" name="sc-last-comm" type="date" value={toDateInput(form.lastCommunicationAt)} onChange={(e) => set('lastCommunicationAt', e.target.value || null)} />
          <Input label="Next Follow-up" name="sc-next-followup" type="date" value={toDateInput(form.nextFollowUpAt)} onChange={(e) => set('nextFollowUpAt', e.target.value || null)} />
        </div>

        <div>
          <label htmlFor="sc-notes" className="mb-1.5 block text-body-sm font-medium text-on-surface">Notes</label>
          <textarea
            id="sc-notes"
            rows={2}
            value={form.notes ?? ''}
            onChange={(e) => set('notes', e.target.value)}
            className="w-full resize-none rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
      </form>
    </Modal>
  );
}
